import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from "@tanstack/react-query";

import { API, graphqlOperation } from "aws-amplify";

import { GraphQLQuery } from "@aws-amplify/api";

import {
  listConnections,
  listChampions,
  searchUser,
  getMutualConnections,
  connectionsByConnectionUserToId,
  notificationsByUserTo,
  championsByUserToId,
  championsByUserFromId,
  getListContext,
} from "@/graphql/queries";

import {
  deleteConnection,
  updateConnection,
  updateFanConnection,
  requestChampions,
  responseChampionRequest,
  manageConnection,
  requestConnection,
  deleteChampion,
  updateIntroductionRequest,
} from "@/graphql/mutations";

import { sortRecordsByKey } from "@/utils/sortGraphqlRecords";

import { IUseRecommendToUser, UserType } from "@/types/recommendation.types";
import { ListConnectionsQuery } from "@/graphql/graphql";
import { useState } from "react";
import { sendRecommendToUser } from "./useRecommendation";
import { useShowModalResult } from "@/components/modal-result";
import { useLocale } from "@/localization";
import { useRouter } from "next/router";
import { userBecomeFan } from "./useUsers";
import { TUserType, userTypeConvert } from "@/utils/helper";
import { useRequestIntroduction } from "./useIntroduction";
import useMyProfileStore from "./useMyProfileStore";
import { useGetMyActivity } from "./useMyActivity";

interface IUpdateFanConnection {
  connectionId: string;
  status: string;
}

interface IRequestChampion {
  targetUserId: string;
  requestMessage: string;
}

interface IResponseRequestChampion {
  championReqId: string;
  status: string;
}

export interface IManageConnection {
  purpose: "CREATE" | "ACCEPTED" | "DECLINED" | "DELETE";
  userType?: UserType;
  userFromType?: UserType;
  referenceID: string;
  message?: string;
  introducerId?: string;
}

export interface IRequestConnection {
  $userId: string;
  $introducerId?: string;
  $message?: string;
  $userFromType: UserType;
  $userToType: UserType;
}

interface IDataUpdateConnection {
  id: string;
  _version: string;
  status: string;
}

interface IDataDeleteConnection {
  id: string;
  _version: string;
}

interface DeleteConnectionMutation {
  deleteConnection: IDataDeleteConnection;
}

interface DeleteConnectionMutation {
  deleteConnection: IDataDeleteConnection;
}

interface DeleteChampionMutation {
  deleteChampion: IDataDeleteConnection;
}

interface UpdateConnectionMutation {
  updateConnection: IDataUpdateConnection;
}

interface IConnections {
  connectionType?: "SUPER_MENTOR" | "MENTOR" | "MENTEE" | "ALLY" | "FANS";
  keyword?: string;
  limit?: number;
  usePagination?: boolean;
  showChampionUserOnly?: boolean;
  showConnectionOnly?: boolean;
  showNonConnectionOnly?: boolean;
  nextToken?: any;
  isRecommendConnection?: boolean;
}

type introReqStatus = "APPROVED" | "REJECTED" | "PENDING";

interface IpayloadUpdateIntroReq {
  _version: number;
  id: string;
  status: introReqStatus;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const fetchConnection = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listConnections, {
      filter: {
        or: [
          {
            connectionUserToId: { eq: id },
            or: [
              { status: { eq: "APPROVED" } }, // full connection
              { userToRequestStatus: { eq: "APPROVED" } }, // approved partial fan connection as userTo
            ],
          },
          {
            connectionUserFromId: { eq: id },
            or: [
              { status: { eq: "APPROVED" } }, // full connection
              { userFromRequestStatus: { eq: "APPROVED" } }, // approved partial fan connection as userFrom
            ],
          },
        ],
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.listConnections ?? {};
  return records;
};

const fetchMunualConnection = async (userId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(getMutualConnections, { userId })
  );

  const records: any = response?.data?.getMutualConnections ?? {};
  return records;
};

const fetchConnectionUser = async (id: string, idUser: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listConnections, {
      filter: {
        or: [
          {
            connectionUserToId: { eq: id },
            connectionUserFromId: { eq: idUser },
          },
          {
            connectionUserToId: { eq: idUser },
            connectionUserFromId: { eq: id },
          },
        ],
        status: { eq: "APPROVED" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.listConnections ?? {};
  return records;
};

const fetchConnectionPending = async (id: string) => {
  const response: any = await API.graphql(
    // graphqlOperation(listConnections, {
    //   filter: {
    //     or: [
    //       { connectionUserToId: { eq: id }, status: { eq: "PENDING" } },
    //       {
    //         connectionUserToId: { eq: id },
    //         userFromType: { eq: "FANS" },
    //         userToRequestStatus: { eq: "PENDING" },
    //       },
    //       {
    //         connectionUserFromId: { eq: id },
    //         userToType: { eq: "FANS" },
    //         userFromRequestStatus: { eq: "PENDING" },
    //       },
    //     ],
    //     _deleted: { ne: true },
    //   },
    // }),
    graphqlOperation(connectionsByConnectionUserToId, {
      connectionUserToId: id,
      filter: {
        or: [{ userToType: { ne: "ALLY" } }, { userToType: { ne: "FANS" } }],
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.connectionsByConnectionUserToId ?? {};
  return records;
};

const fetchChampions = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listChampions, {
      filter: {
        userFromId: { eq: id },
        status: { eq: "APPROVED" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.listChampions ?? {};
  return records;
};

const fetchChampionsPending = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listChampions, {
      filter: {
        userToId: { eq: id },
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.listChampions ?? {};
  return records;
};

const fetchChampionsPendingByUserTo = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(championsByUserToId, {
      userToId: id,
      filter: {
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any[] = response?.data?.championsByUserToId?.items || [];
  return records;
};

const fetchChampionsPendingByUserFrom = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(championsByUserFromId, {
      userFromId: id,
      filter: {
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any[] = response?.data?.championsByUserFromId?.items || [];
  return records;
};

const fetchChampionsNotification = async (id: string) => {
  const response: any = await API.graphql(
    graphqlOperation(listChampions, {
      filter: {
        or: [{ userToId: { eq: id } }, { userFromId: { eq: id } }],
        status: { eq: "PENDING" },
        _deleted: { ne: true },
      },
    })
  );

  const records: any = response?.data?.listChampions ?? {};
  return records;
};

const fetchManageConnection = async (userId: string) => {
  const response: any = await API.graphql(
    graphqlOperation(notificationsByUserTo, {
      userTo: userId,
      filter: {
        referenceType: { eq: "MANAGE_CONNECTION" },
        or: [{ status: { eq: "UNREAD" } }, { status: { eq: "READ" } }],
        _deleted: { ne: true },
      },
    })
  );
  const records: any[] = response?.data?.notificationsByUserTo?.items ?? [];
  return sortRecordsByKey(records, "createdAt", false);
};

const fetchChangeConnectionRequest = async (
  userTo: string,
  userFrom: string
) => {
  const response: any = await API.graphql(
    graphqlOperation(notificationsByUserTo, {
      userTo,
      filter: {
        userFrom: { eq: userFrom },
        referenceType: { eq: "MANAGE_CONNECTION" },
        or: [{ status: { eq: "UNREAD" } }, { status: { eq: "READ" } }],
        _deleted: { ne: true },
      },
    })
  );
  const records: any[] = response?.data?.notificationsByUserTo?.items ?? [];
  return sortRecordsByKey(records, "createdAt", false);
};

const fetchTeaPosts = async (
  id: string,
  isHR: boolean,
  pageParam?: string | undefined
) => {
  try {
    const response: any = await API.graphql(
      graphqlOperation(getListContext, {
        id,
        context: "TEA_FEATURE", // Specify the context for tea posts
        limit: 8,
        isHR: isHR || false, // Pass isHR flag
        usePagination: true, // Enable pagination
        nextToken: pageParam || null, // Use pageParam for pagination
      })
    );
    let records = response?.data?.getListContext?.teaPosts || [];
    let nextToken = response?.data?.getListContext?.nextToken || null;
    const filteredRecords = records.filter((post: any) => !post._deleted);
    return { data: filteredRecords, nextToken };
  } catch (error) {
    throw new Error("Error fetching tea posts");
  }
};

const useGetTeaPosts = (id: string, isHR: boolean) => {
  return useInfiniteQuery({
    queryKey: [`KEY_GET_TEA_POSTS`, id],
    enabled: true,
    queryFn: ({ pageParam = null }) => fetchTeaPosts(id, isHR, pageParam),
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
  });
};

const useGetConnection = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CONNECTION`, id],
    queryFn: () => fetchConnection(id),
  });
};

const useGetChangeConnectionRequest = (userTo: string, userFrom: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHANGE_CONNECTION_REQUEST`, userTo, userFrom],
    queryFn: () => fetchChangeConnectionRequest(userTo, userFrom),
  });
};

const useGetMutualConnection = (userId: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CONNECTION_MUTUAL`, userId],
    queryFn: () => fetchMunualConnection(userId),
  });
};

const useGetConnectionUser = (id: string, userId: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CONNECTION_USER`, id, userId],
    queryFn: () => fetchConnectionUser(id, userId),
  });
};

const useGetChampions = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHAMPIONS`, id],
    queryFn: () => fetchChampions(id),
  });
};

const useGetChampionsPending = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHAMPIONS_PENDING`, id],
    queryFn: () => fetchChampionsPending(id),
  });
};

const useGetChampionsPendingByUserTo = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHAMPIONS_PENDING_BY_USER_TO`, id],
    queryFn: () => fetchChampionsPendingByUserTo(id),
  });
};

const useGetChampionsPendingByUserFrom = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHAMPIONS_PENDING_BY_USER_FROM`, id],
    queryFn: () => fetchChampionsPendingByUserFrom(id),
  });
};

const useGetChampionsNotification = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_CHAMPIONS_NOTIFICATION`, id],
    queryFn: () => fetchChampionsNotification(id),
  });
};

const useGetConnectionPending = (id: string) => {
  return useQuery({
    queryKey: [`KEY_GET_PENDING_CONNECTION`, id],
    queryFn: () => fetchConnectionPending(id),
  });
};

const useDeleteConnection = async (id: string, version: string) => {
  const payload: IDataDeleteConnection = {
    id,
    _version: version,
  };

  return await API.graphql<GraphQLQuery<DeleteConnectionMutation>>({
    query: deleteConnection,
    variables: { input: payload },
  });
};

const useDeleteChampion = async (id: string, version: string) => {
  const payload: IDataDeleteConnection = {
    id,
    _version: version,
  };

  return await API.graphql<GraphQLQuery<DeleteChampionMutation>>({
    query: deleteChampion,
    variables: { input: payload },
  });
};

const useGetManageConnection = (id: string) => {
  return useQuery({
    queryKey: ["KEY_GET_MANAGE_CONNECTION", id],
    queryFn: () => fetchManageConnection(id),
  });
};

const useUpdateConnection = async (payload: IDataUpdateConnection) => {
  return await API.graphql<GraphQLQuery<UpdateConnectionMutation>>({
    query: updateConnection,
    variables: { input: payload },
  });
};

const useUpdateFanConnection = async (id: string, status: string) => {
  const payload: IUpdateFanConnection = {
    connectionId: id,
    status,
  };

  return await API.graphql<GraphQLQuery<IUpdateFanConnection>>({
    query: updateFanConnection,
    variables: payload,
  });
};

const useRequestChampion = async (id: string, message: string) => {
  const payload: IRequestChampion = {
    targetUserId: id,
    requestMessage: message,
  };

  return await API.graphql<GraphQLQuery<IRequestChampion>>({
    query: requestChampions,
    variables: payload,
  });
};

const useResponseRequestChampion = async (id: string, status: string) => {
  const payload: IResponseRequestChampion = {
    championReqId: id,
    status,
  };

  return await API.graphql<GraphQLQuery<IResponseRequestChampion>>({
    query: responseChampionRequest,
    variables: payload,
  });
};

const useManageConnection = async (payload: IManageConnection) => {
  return await API.graphql<GraphQLQuery<IManageConnection>>({
    query: manageConnection,
    variables: payload,
  });
};

const useRequestConnection = async (payload: IRequestConnection) => {
  return await API.graphql<GraphQLQuery<IRequestConnection>>({
    query: requestConnection,
    variables: payload,
  });
};

const useGetConnections = (
  payload?: IConnections,
  enabled?: boolean,
  organizationGroup?: string
) => {
  let req: IConnections = {
    keyword: payload?.keyword,
    limit: payload?.limit,
    showChampionUserOnly: payload?.showChampionUserOnly,
    showConnectionOnly: payload?.showConnectionOnly,
    showNonConnectionOnly: payload?.showNonConnectionOnly,
    usePagination: payload?.showNonConnectionOnly,
    isRecommendConnection: payload?.isRecommendConnection || false,
  };

  if (payload?.nextToken) {
    req = {
      ...req,
      nextToken: payload?.nextToken,
    };
  }

  if (payload?.connectionType) {
    req = {
      ...req,
      connectionType: payload?.connectionType,
    };
  }

  return useQuery({
    queryKey: [`KEY_GET_SEARCH_CONNECTIONS`, req], // Corrected queryKey
    queryFn: async () => {
      const records: any = await API.graphql<GraphQLQuery<any>>({
        query: searchUser,
        variables: {
          input: {
            ...req,
          },
        },
      });
      const filteredByOrg = organizationGroup
        ? records?.data?.searchUser?.data?.filter(
            (x: any) => x?.user?.organizationGroup === organizationGroup
          )
        : records?.data?.searchUser?.data;
      return {
        nextToken: records?.data?.searchUser?.nextToken,
        data: sortRecordsByKey(filteredByOrg, "user.firstName") as any[],
      };
    },
    enabled: enabled === undefined || enabled,
    initialData: { data: [], nextToken: null },
  });
};

const useSearchPendingUserChampion = () => {
  return useQuery({
    queryKey: [`KEY_GET_SEARCH_CONNECTIONS_CHAMPION`], // Corrected queryKey
    queryFn: async () => {
      const records: any = await API.graphql<GraphQLQuery<any>>({
        query: searchUser,
        variables: {
          input: {
            showPendingChampionUserOnly: true,
          },
        },
      });
      return records?.data?.searchUser?.data;
    },
  });
};

const useUpdateIntroductionRequest = (status: introReqStatus) => {
  return useMutation({
    mutationKey: [`KEY_UPDATE_INTRO_REQ`, status], // Corrected queryKey
    mutationFn: async (payload: IpayloadUpdateIntroReq) => {
      const { _version, status, id } = payload;
      const records: any = await API.graphql<GraphQLQuery<any>>({
        query: updateIntroductionRequest,
        variables: {
          input: { _version, id, status },
        },
      });
      return records;
    },
  });
};

const prefetchGetConnections = (payload?: IConnections) => {
  let req: IConnections = {
    keyword: payload?.keyword,
    limit: payload?.limit,
    showChampionUserOnly: payload?.showChampionUserOnly,
    showConnectionOnly: payload?.showConnectionOnly,
    showNonConnectionOnly: payload?.showNonConnectionOnly,
    nextToken: payload?.nextToken,
    connectionType: payload?.connectionType,
  };

  if (payload?.connectionType) {
    req = {
      ...req,
      connectionType: payload?.connectionType,
    };
  }
  return queryClient.prefetchQuery({
    queryKey: ["PREFETCH_LIST_CONNECTION"],
    queryFn: async () => {
      const records: any = await API.graphql<GraphQLQuery<any>>({
        query: searchUser,
        variables: {
          input: {
            ...req,
          },
        },
      });
      return sortRecordsByKey(
        records?.data?.searchUser?.data,
        "user.firstName"
      );
    },
  });
};

export const useGetOtherPeopleConnections = (id: string) => {
  return useQuery({
    queryKey: ["LIST_OTHER_USER_CONNECTIONS", id],
    queryFn: async () => {
      const recordsFrom = await API.graphql<GraphQLQuery<ListConnectionsQuery>>(
        {
          query: listConnections,
          variables: {
            filter: {
              connectionUserFromId: { contains: id },
            },
          },
        }
      );
      const recordsTo = await API.graphql<GraphQLQuery<ListConnectionsQuery>>({
        query: listConnections,
        variables: {
          filter: {
            connectionUserToId: { contains: id },
          },
        },
      });
      const dataFrom = recordsFrom.data?.listConnections?.items || [];
      const dataTo = recordsTo.data?.listConnections?.items || [];
      return [...dataFrom, ...dataTo];
    },
    enabled: false,
  });
};

export const useRecommendConnection = () => {
  const [isProcess, setIsProcess] = useState<{
    purpose: IUseRecommendToUser["purpose"];
    id: string;
  } | null>(null);
  const { successModal, errorModal } = useShowModalResult();
  const router = useRouter();
  const { userView } = useMyProfileStore();

  const {
    locale: {
      notification: { recommended_resources: recommended_resources_locale },
    },
  } = useLocale();

  const handleRecommendConnection = async (
    payload: IUseRecommendToUser,
    contextName: string,
    contextId: string,
    callback?: () => void
  ) => {
    const mentorshipType: Record<string, string[]> = {
      SUPER_MENTOR: ["SUPER_MENTOR", "MENTEE"],
      SPONSOR: ["MENTEE", "SUPER_MENTOR"],
      MENTOR: ["MENTOR", "MENTEE"],
      MENTEE: ["MENTEE", "MENTOR"],
      ALLY: ["ALLY", "ALLY"],
    };

    setIsProcess({ purpose: payload?.purpose, id: contextId });

    const getTargetType = (notifDDescription: string): string => {
      const strSplit = notifDDescription.split(" ");
      const lastStr = strSplit[strSplit.length - 1];

      if (
        [
          "SUPER_MENTOR",
          "SPONSOR",
          "SPONSOR.",
          "MENTOR",
          "MENTOR.",
          "MENTEE",
          "MENTEE.",
          "ALLY",
          "SPONSOR.",
        ].includes(lastStr)
      ) {
        if (lastStr === "SPONSOR.") {
          return "SPONSOR";
        }
        if (lastStr === "MENTOR.") {
          return "MENTOR";
        }
        if (lastStr === "MENTEE.") {
          return "MENTEE";
        }

        return lastStr;
      }

      return "ALLY";
    };

    const handleReqConnection = async () => {
      const fromToMentorship =
        mentorshipType[getTargetType(payload?.notifDescription as string)];

      try {
        const payloade = {
          userId: contextId,
          message: `${userView?.user?.firstName} ${
            userView?.user?.lastName
          } sent a connection request as ${userTypeConvert(
            payload?.userFromType as any
          )}`,
          userFromType: fromToMentorship[1],
          userToType: fromToMentorship[0],
        };

        await useRequestIntroduction(payloade);
      } catch (error: any) {
        errorModal({
          title: "Request Connection Failed",
          body:
            error?.errors?.[0]?.message ||
            "Failed to request connection , please try again.",
          show: true,
          type: "error",
        });
      }
    };

    try {
      await sendRecommendToUser(payload);

      if (payload?.purpose === "DECLINED") {
        successModal({
          show: true,
          title:
            recommended_resources_locale.confirmation
              .S_decline_connection_request.title,
          body: recommended_resources_locale.confirmation.S_decline_connection_request.body.replaceAll(
            "{{name}}",
            contextName
          ),
          onClickOK: () => callback && callback(),
        });
      } else {
        const fromToMentorship =
          mentorshipType[getTargetType(payload?.notifDescription || "")];

        await handleReqConnection();

        successModal({
          show: true,
          title: `Request Sent.`,
          body: `Congrats! You earned more points by successfully sending a mentorship request for ${contextName} to become your ${userTypeConvert(
            fromToMentorship?.[0] as TUserType
          )}. You're welcome to cancel this request at any time so you can connect to ${contextName}, but mentorships earn more points than connections.`,
          onClickOK: () => callback && callback(),
        });
        // successModal({
        //   show: true,
        //   title:
        //   recommended_resources_locale.confirmation
        //     .S_decline_connection_request.title,
        // body: recommended_resources_locale.confirmation.S_decline_connection_request.body.replaceAll(
        //   "{{name}}",
        //   contextName,
        // ),
        // });
      }
    } catch (error: any) {
      errorModal({
        title: "Error",
        body:
          error?.errors?.[0]?.message ||
          `${payload?.purpose?.toLocaleLowerCase()} ${payload?.type?.toLocaleLowerCase()} failed to process, please try again`,
        show: true,
        type: "error",
      });
    } finally {
      setIsProcess(null);
    }
  };

  return { handleRecommendConnection, isProcess };
};

export const useWaveHandler = () => {
  const [isProcess, setIsProcess] = useState<{
    id: string;
    purpose: "ACCEPT" | "DECLINED";
  } | null>(null);
  const { successModal, errorModal } = useShowModalResult();
  const { refetch: callGetMyActivity } = useGetMyActivity();

  const waveBack = async (userId: string, username: string) => {
    setIsProcess({ purpose: "ACCEPT", id: userId });
    try {
      await userBecomeFan(userId, true);
      successModal({
        show: true,
        title: "Connection successfully made",
        body: `Congrats! You earned more points by accepting the connection request from ${username}. You are now connected with ${username}.`,
      });
      callGetMyActivity();
    } catch (error: any) {
      errorModal({
        title: "Wave failed",
        body:
          error?.errors?.[0]?.message ||
          "There was a problem when wave process, please try again.",
        show: true,
        type: "error",
      });
    } finally {
      setIsProcess(null);
    }
  };

  return { waveBack, isProcess };
};

export const useConnectionApproveDeclined = () => {
  const [isProcess, setIsProcess] = useState<null | {
    id: string;
    type: "APPROVE" | "DECLINE";
  }>(null);
  const { successModal, errorModal } = useShowModalResult();

  const handleRejectConnection = async (
    id: string,
    version: string,
    status: any,
    name: string,
    toType: string,
    fromType: string
  ) => {
    setIsProcess({ id: id, type: "DECLINE" });
    try {
      if (status === null) {
        await useUpdateFanConnection(id, "REJECTED");
      } else {
        await useDeleteConnection(id, version);
      }

      successModal({
        show: true,
        title: "Connection Declined",
        body: `You declined the request to be ${name}'s ${userTypeConvert(
          fromType as never,
          toType as never
        )}.`,
      });
    } catch (error: any) {
      errorModal({
        title: "Error",
        body:
          error?.errors?.[0]?.message ||
          "Failed to reject connection , please try again.",
        show: true,
        type: "error",
      });
    } finally {
      setIsProcess(null);
    }
  };

  const handleApproveConnection = async (
    id: string,
    _version: string,
    status: any,
    name: string,
    toType: string,
    fromType: string
  ) => {
    setIsProcess({ id, type: "APPROVE" });
    try {
      const payload = {
        id,
        status: "APPROVED",
        _version,
      };

      if (status === null) {
        await useUpdateFanConnection(id, "APPROVED");
      } else {
        await useUpdateConnection(payload);
      }

      successModal({
        show: true,
        title: "You Are Now Connected",
        body: `You are now connected with ${name} as a ${userTypeConvert(
          fromType as never,
          toType as never
        )}.`,
      });

      setIsProcess(null);
    } catch (error: any) {
      errorModal({
        title: "Error",
        body:
          error?.errors?.[0]?.message ||
          "Failed to approved connection , please try again.",
        show: true,
        type: "error",
      });

      setIsProcess(null);
    }
  };

  return { isProcess, handleApproveConnection, handleRejectConnection };
};

export const useRelationshipUpgrades = () => {
  const [isProcess, setIsProcess] = useState<null | {
    purpose: "CREATE" | "ACCEPTED" | "DECLINED";
    id: string;
  }>(null);

  const { successModal, errorModal } = useShowModalResult();

  const handleActionManageConnection = async (
    referenceID: string,
    purpose: "CREATE" | "ACCEPTED" | "DECLINED",
    name?: string
  ) => {
    setIsProcess({ id: referenceID, purpose });

    const payload = {
      purpose,
      referenceID,
    };

    try {
      await useManageConnection(payload);

      if (purpose === "ACCEPTED") {
        successModal({
          show: true,
          title: "Relationship upgraded",
          body: `${name}`,
        });
      }

      if (purpose === "DECLINED") {
        successModal({
          show: true,
          title: "Decline Relationship Upgrade",
          body: `${name}`,
        });
      }

      setIsProcess(null);
    } catch (error: any) {
      errorModal({
        title: "Error",
        body:
          error?.errors?.[0]?.message ||
          `${purpose?.toLocaleLowerCase()}  change of type connection failed , please try again.`,
        show: true,
        type: "error",
      });

      setIsProcess(null);
    }
  };
  return { handleActionManageConnection, isProcess };
};

const fetchEligibleUser = async (
  type: "EVENT" | "CIRCLE",
  id: string,
  isHR?: boolean
) => {
  let context =
    type === "EVENT" ? "ELIGIBLE_USER_TO_EVENT" : "ELIGIBLE_USER_TO_CIRCLE";
  let key = type === "EVENT" ? "eligibleUsersToEvent" : "eligibleUsersToCircle";
  const response: any = await API.graphql(
    graphqlOperation(getListContext, {
      id,
      context,
      isHR: isHR || false,
    })
  );
  const data = response?.data?.getListContext?.[key] || [];
  return data;
};

export const useEligibleUser = (
  type: "EVENT" | "CIRCLE",
  id: string,
  isHR?: boolean
) => {
  return useQuery({
    queryKey: [`KEY_ELIGIBLE_USER_${type}`, id],
    queryFn: async () => fetchEligibleUser(type, id, isHR),
    enabled: !!id,
    refetchOnWindowFocus: true,
  });
};

export {
  useGetConnections,
  useGetConnection,
  useGetMutualConnection,
  useGetConnectionUser,
  useGetChampions,
  useDeleteConnection,
  useDeleteChampion,
  useUpdateConnection,
  useGetConnectionPending,
  useUpdateFanConnection,
  useRequestChampion,
  useGetChampionsPending,
  useResponseRequestChampion,
  useManageConnection,
  useRequestConnection,
  useGetManageConnection,
  useSearchPendingUserChampion,
  useUpdateIntroductionRequest,
  useGetChampionsNotification,
  useGetChangeConnectionRequest,
  useGetChampionsPendingByUserTo,
  useGetChampionsPendingByUserFrom,
  prefetchGetConnections,
  useGetTeaPosts,
};
